"use client";

import useConstantValueList from "@/hooks/useConstantValueList";
import useApiSubmit from "@/hooks/useApiSubmit";
import useServiceList from "@/hooks/useServiceList";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Field,
  Fieldset,
  Form,
  PhoneField,
  Radio,
  RadioGroup,
  Select,
  TextArea,
  Typography,
} from "@repo/ui";
import { usePathname, useRouter } from "next/navigation";
import { ComponentProps, useEffect, useMemo, useRef } from "react";
import * as Yup from "yup";
import { useAppDispatch } from "@/store/hooks";
import { setFullName } from "@/store/features/appSlice";
import TermsCheckbox from "./TermsCheckbox";
import { useCountryCode } from "@/hooks/useCountryCode";
import useHashRoute from "@/hooks/useHashRoute";
import { FormikProps } from "formik";

const BG_COLORS = {
  white: "bg-white",
  primary: "bg-primary-main/5",
} as const;

const DEFAULT_FORM_VALUES = {
  fullName: "",
  contactNumber: "",
  email: "",
  serviceId: [] as string[],
  message: "",
  teamSize: 0,
  whoAreWe: "173",
  enquiryType: "COURSE_REQUEST_TRAINING",
  terms: "false",
};

const QueryFormSchema = Yup.object().shape({
  fullName: Yup.string()
    .required()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  contactNumber: Yup.string()
    .required()
    .min(10, "Too Short!")
    .max(16, "Too Long!")
    .required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
});

type QueriesFormProps = Pick<ComponentProps<typeof Fieldset>, "columns"> &
  Pick<ComponentProps<typeof Card>, "elevation"> & {
    color?: keyof typeof BG_COLORS;
    formHeading?: string;
    buttonLabel?: string;
    formDescription?: string;
    serviceId?: string;
    id?: string;
  };

function QueriesForm({
  columns = 1,
  color = "white",
  elevation,
  formHeading = "Enquire us!",
  buttonLabel = "Enquire now",
  formDescription,
  serviceId,
  id,
}: QueriesFormProps) {
  const router = useRouter();
  const pathname = usePathname();
  const formikRef = useRef<FormikProps<any>>(null);
  const { data: services } = useServiceList();
  const { data: whoWeAre } = useConstantValueList();
  const dispatch = useAppDispatch();
  const { country } = useCountryCode();
  const cardBgColor = BG_COLORS[color] ?? BG_COLORS.white;

  const { handleSubmit } = useApiSubmit({
    onSuccess: (data) => {
      dispatch(setFullName(data.fullName));
      router.push("/thank-you-for-enquiry");
    },
  });

  const hash = useHashRoute();

  const initialValues = useMemo(
    () => ({
      ...DEFAULT_FORM_VALUES,
      serviceId: serviceId ? [serviceId] : [],
    }),
    [serviceId, hash]
  );

  useEffect(() => {
    const newWhoAreWe = hash === "request-for-group-training" ? "172" : "173";
    if (formikRef.current) {
      if (formikRef.current.values.whoAreWe !== newWhoAreWe) {
        formikRef.current.setFieldValue("whoAreWe", newWhoAreWe);
      }
    }
  }, [hash, formikRef]);

  const formContent = useMemo(
    () => (
      <Form
        initialValues={initialValues}
        validationSchema={QueryFormSchema}
        onSubmit={handleSubmit}
        ref={formikRef}
      >
        {({ values, handleChange, setFieldValue, isSubmitting }) => (
          <>
            <Fieldset columns={columns}>
              <Field
                required
                name="fullName"
                label="Full Name"
                value={values.fullName}
                onChange={handleChange}
              />
              <PhoneField
                label="Phone Number"
                name="contactNumber"
                required
                value={values.contactNumber}
                onChange={(phone: string) =>
                  setFieldValue("contactNumber", phone)
                }
                defaultCountry={country}
              />
              <Field
                required
                label="Email ID"
                name="email"
                value={values.email}
                onChange={handleChange}
              />
              {!pathname.includes("/course/") && (
                <Select
                  label="Courses"
                  multiple
                  name="serviceId"
                  options={services}
                  value={values.serviceId}
                  onChange={(value: string[]) =>
                    setFieldValue("serviceId", value)
                  }
                />
              )}

              <RadioGroup
                label="Enquiry For"
                value={values.whoAreWe}
                name="whoAreWe"
                defaultValue={values.whoAreWe}
                onValueChange={(value) => setFieldValue("whoAreWe", value)}
              >
                {whoWeAre.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
              {values.whoAreWe === "172" && (
                <Field
                  label="Team Size"
                  name="teamSize"
                  value={values.teamSize}
                  onChange={handleChange}
                  required
                  type="number"
                  placeholder="No. of Pax"
                />
              )}
              <TextArea
                name="message"
                label="Message"
                required
                colSpan={2}
                value={values.message}
                placeholder="Type your message here..."
                onChange={handleChange}
              />
            </Fieldset>

            <TermsCheckbox
              onCheckedChange={(value) => setFieldValue("terms", value)}
              value={values.terms}
            />

            <div className="text-right mt-4">
              <Button type="submit" color="primary" loading={isSubmitting}>
                {buttonLabel}
              </Button>
            </div>
            {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
          </>
        )}
      </Form>
    ),
    [
      columns,
      country,
      handleSubmit,
      services,
      whoWeAre,
      buttonLabel,
      initialValues,
      pathname,
    ]
  );

  return (
    <Card className={`!${cardBgColor} border`} elevation={elevation} id={id}>
      <CardHeader className="!p-8 !pb-6">
        <Typography variant="h4" render={<div />}>
          {formHeading}
        </Typography>
        {formDescription && (
          <Typography paragraph className="!text-sm text-gray-500">
            {formDescription}
          </Typography>
        )}
      </CardHeader>
      <CardContent className="!p-8 !pt-0">{formContent}</CardContent>
    </Card>
  );
}

export default QueriesForm;
